import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";

// Workflow Compilation — the Workflows-tab UI contract (goal #33, Phase E,
// docs/optimization/WORKFLOW-COMPILATION-PRD.md §UI + product surface
// docs/optimization/COMPILED-WORKFLOW-SURFACE-PRD.md).
//
// Reads + mutations over the v137/v138 compile_* tables:
//   • list / get / adjudicate — original Phase E surface
//   • setStatus / source / epoch / promote / demote / autorun — product surface
//
// The server route reads through @excellent/core/compile pure engine so routing
// split + holdout accuracy + per-axis coverage are computed from the SAME
// interpreter that will route live work.

// ── shared scalars ────────────────────────────────────────────────────────────

const compilationStatus = z.enum([
  "defined",
  "sourcing",
  "compiling",
  "compiled",
  "paused",
]);

const ruleStatus = z.enum(["candidate", "accepted", "rejected", "retired"]);

/** Rule-count-by-status rollup for a workflow's deterministic layer. */
const ruleCounts = z.object({
  candidate: z.number(),
  accepted: z.number(),
  rejected: z.number(),
  retired: z.number(),
});

/** Routing distribution over the corpus (auto = committed no-review; tier2 =
 *  abstain band that batches to the cheap model; human = surfaced dispute). */
const routing = z.object({
  /** True once a corpus + accepted rules exist — else the rates are all 0. */
  scored: z.boolean(),
  auto: z.number(),
  tier2: z.number(),
  human: z.number(),
  /** Mean per-axis accuracy on the sealed holdout split. */
  holdoutAccuracy: z.number(),
  /** Mean per-axis accuracy on the train split. */
  trainAccuracy: z.number(),
});

// ── list ──────────────────────────────────────────────────────────────────────

const compilationListItem = z.object({
  slug: z.string(),
  title: z.string(),
  /** One operator-language sentence (the spec charter, first line). */
  charter: z.string(),
  status: compilationStatus,
  /** False when the workflow is registry-only (no compile_workflows row yet). */
  inStore: z.boolean(),
  ruleCounts,
  corpusTotal: z.number(),
  routing,
  /** Highest epoch number recorded, or null if none run. */
  lastEpoch: z.number().nullable(),
  unresolvedDisputes: z.number(),
  /** `excellent-mcp compile source <slug>` — the honest empty-state command. */
  sourceCommand: z.string(),
});
export type CompilationListItem = z.infer<typeof compilationListItem>;

const compilationListResult = z.object({
  items: z.array(compilationListItem),
});
export type CompilationListResult = z.infer<typeof compilationListResult>;

const list: Operation<void, void, void, CompilationListResult> = {
  method: "GET",
  path: () => "/api/compilations",
  response: compilationListResult,
};

// ── get (detail) ──────────────────────────────────────────────────────────────

const axisView = z.object({
  key: z.string(),
  blast: z.enum(["low", "high"]),
  noneAllowed: z.boolean(),
  deferred: z.boolean(),
  values: z.array(z.string()),
});
export type AxisView = z.infer<typeof axisView>;

const ruleView = z.object({
  id: z.string(),
  axis: z.string(),
  status: ruleStatus,
  confidence: z.number(),
  /** correct/committed on train + holdout, from the rule's stats ledger. */
  trainN: z.number(),
  trainPrecision: z.number().nullable(),
  holdoutN: z.number(),
  holdoutPrecision: z.number().nullable(),
  provenanceEpoch: z.number(),
  rationale: z.string(),
});
export type RuleView = z.infer<typeof ruleView>;

/** Confidence-calibration bin (observed accuracy per emitted-confidence band). */
const calibrationRow = z.object({
  axis: z.string(),
  lo: z.number(),
  hi: z.number(),
  n: z.number(),
  accuracy: z.number(),
});
export type CalibrationRow = z.infer<typeof calibrationRow>;

const epochView = z.object({
  epoch: z.number(),
  startedAt: z.string().nullable(),
  trainBefore: z.number(),
  trainAfter: z.number(),
  holdoutBefore: z.number(),
  holdoutAfter: z.number(),
  proposed: z.number(),
  acceptedCount: z.number(),
  rejectedCount: z.number(),
  /** Rejected-edit buffer summaries — "editSummary — reason". */
  rejectedReasons: z.array(z.string()),
  tier2Labeled: z.number(),
  humanQueued: z.number(),
});
export type EpochView = z.infer<typeof epochView>;

const disputeView = z.object({
  exampleKey: z.string(),
  axis: z.string(),
  blast: z.enum(["low", "high"]),
  title: z.string(),
  description: z.string(),
  difficulty: z.string().nullable(),
  /** The two UNATTRIBUTED candidate answers (rendered without saying which is
   *  the recorded label vs the model's). Doing nothing keeps `valueLlm`. */
  valueGenerated: z.string(),
  valueLlm: z.string(),
  /** Corpus origin of the disputed example. Synthetic = a training example the
   *  compiler generated (batch-groupable curriculum refinement); real = drawn
   *  from live workspace data (NEVER batch-grouped — surfaced individually).
   *  Optional/additive: the current engine only ever queues synthetic disputes,
   *  so an absent value is treated as synthetic by the UI. */
  origin: z.enum(["synthetic", "real"]).optional(),
});
export type DisputeView = z.infer<typeof disputeView>;

const corpusSampleRow = z.object({
  exampleKey: z.string(),
  title: z.string(),
  split: z.enum(["train", "holdout"]),
  origin: z.enum(["synthetic", "real"]),
  difficulty: z.string().nullable(),
  /** Effective truth per axis (generated < llm < human), pretty-joined. */
  truthSummary: z.string(),
  /** Which layers carry a label: any of generated/llm/human. */
  layers: z.array(z.enum(["generated", "llm", "human"])),
});
export type CorpusSampleRow = z.infer<typeof corpusSampleRow>;

const corpusView = z.object({
  total: z.number(),
  bySplit: z.object({ train: z.number(), holdout: z.number() }),
  byOrigin: z.object({ synthetic: z.number(), real: z.number() }),
  byDifficulty: z.record(z.string(), z.number()),
  /** First 50 examples (most-recently-updated first). */
  sample: z.array(corpusSampleRow),
});
export type CorpusView = z.infer<typeof corpusView>;

/** Per-axis holdout coverage from the pure engine scorer (not holdoutN sum). */
const axisCoverageRow = z.object({
  axis: z.string(),
  /** Accepted rules on this axis. */
  rules: z.number(),
  /** Fraction of holdout examples where a rule COMMITTED on this axis ∈ [0,1]. */
  coverage: z.number(),
});
export type AxisCoverageRow = z.infer<typeof axisCoverageRow>;

/** Promotion state — Live routing is on iff `at` is non-null. */
const promotedView = z.object({
  at: z.string().nullable(),
  by: z.string().nullable(),
  taskId: z.number().nullable(),
  rulesVersion: z.number().nullable(),
});
export type PromotedView = z.infer<typeof promotedView>;

/** Workspace-scoped compile autorun config (agents.compileAutorun). */
const autorunView = z.object({
  enabled: z.boolean(),
  budgetTokens: z.number(),
});
export type AutorunView = z.infer<typeof autorunView>;

const compilationDetail = z.object({
  slug: z.string(),
  title: z.string(),
  charter: z.string(),
  status: compilationStatus,
  inStore: z.boolean(),
  autoThreshold: z.number(),
  axes: z.array(axisView),
  ruleCounts,
  corpusTotal: z.number(),
  routing,
  lastEpoch: z.number().nullable(),
  unresolvedDisputes: z.number(),
  sourceCommand: z.string(),
  rules: z.array(ruleView),
  calibration: z.array(calibrationRow),
  epochs: z.array(epochView),
  disputes: z.array(disputeView),
  corpus: corpusView,
  // Additive product-surface fields (COMPILED-WORKFLOW-SURFACE-PRD §3).
  axisCoverage: z.array(axisCoverageRow),
  promoted: promotedView,
  autorun: autorunView,
  lastEpochStartedAt: z.string().nullable(),
});
export type CompilationDetail = z.infer<typeof compilationDetail>;

const slugParams = z.object({ slug: z.string().min(1).max(80) });
type SlugParams = z.infer<typeof slugParams>;

const get: Operation<SlugParams, void, void, CompilationDetail> = {
  method: "GET",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}`,
  params: slugParams,
  response: compilationDetail,
};

// ── adjudicate ────────────────────────────────────────────────────────────────

const adjudicateBody = z.object({
  /** The example to settle. Required — the single-example form uses this alone,
   *  and it doubles as the representative key when a batch is supplied. */
  exampleKey: z.string().min(1).max(200),
  /** OPTIONAL batch (cluster adjudication): settle the SAME (axis, value) across
   *  every listed example in one transaction. `exampleKey` is unioned in, so a
   *  batch caller need not repeat it. Additive — a single-example caller omits
   *  this field entirely and the write covers just `exampleKey`. */
  exampleKeys: z.array(z.string().min(1).max(200)).min(1).max(1000).optional(),
  axis: z.string().min(1).max(80),
  /** The human's committed answer; null = "none / ∅" for a none-allowed axis. */
  value: z.string().max(200).nullable(),
});
export type AdjudicateBody = z.infer<typeof adjudicateBody>;

const adjudicateResult = z.object({
  ok: z.boolean(),
  /** Present on refusal (unknown axis / example / value out of vocabulary). */
  reason: z.string().optional(),
  /** Batch form only: how many examples received the verdict. Omitted by the
   *  single-example form. */
  written: z.number().optional(),
});
export type AdjudicateResult = z.infer<typeof adjudicateResult>;

const adjudicate: Operation<SlugParams, void, AdjudicateBody, AdjudicateResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/adjudicate`,
  params: slugParams,
  body: adjudicateBody,
  response: adjudicateResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

// ── status (pause / resume compile workflow) ──────────────────────────────────

const setStatusBody = z.object({
  status: z.enum(["paused", "compiling", "compiled", "sourcing", "defined"]),
});
export type SetStatusBody = z.infer<typeof setStatusBody>;

const setStatusResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  status: compilationStatus.optional(),
});
export type SetStatusResult = z.infer<typeof setStatusResult>;

const setStatus: Operation<SlugParams, void, SetStatusBody, SetStatusResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/status`,
  params: slugParams,
  body: setStatusBody,
  response: setStatusResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

// ── source / epoch (detached MCP dispatch) ────────────────────────────────────

const sourceBody = z.object({
  n: z.number().int().positive().max(5000).optional(),
});
export type SourceBody = z.infer<typeof sourceBody>;

const dispatchResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  /** True when a detached MCP CLI child was spawned (or the injectable seam accepted). */
  dispatched: z.boolean().optional(),
});
export type DispatchResult = z.infer<typeof dispatchResult>;

const source: Operation<SlugParams, void, SourceBody, DispatchResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/source`,
  params: slugParams,
  body: sourceBody,
  response: dispatchResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

const epoch: Operation<SlugParams, void, void, DispatchResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/epoch`,
  params: slugParams,
  response: dispatchResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

// ── promote / demote (live routing) ───────────────────────────────────────────

const promoteBody = z.object({
  taskId: z.number().int().positive(),
});
export type PromoteBody = z.infer<typeof promoteBody>;

const promoteResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  detail: z.string().optional(),
  promoted: promotedView.optional(),
});
export type PromoteResult = z.infer<typeof promoteResult>;

const promote: Operation<SlugParams, void, PromoteBody, PromoteResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/promote`,
  params: slugParams,
  body: promoteBody,
  response: promoteResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

const demoteResult = z.object({
  ok: z.boolean(),
  reason: z.string().optional(),
  wasPromoted: z.boolean().optional(),
});
export type DemoteResult = z.infer<typeof demoteResult>;

const demote: Operation<SlugParams, void, void, DemoteResult> = {
  method: "POST",
  path: (p) => `/api/compilations/${encodeURIComponent(p.slug)}/demote`,
  params: slugParams,
  response: demoteResult,
  invalidates: [list, scoped(get, (args: Args<{ slug: string }, void, unknown>) => ({ slug: args.params.slug }))],
};

// ── autorun (workspace-scoped agents.compileAutorun) ──────────────────────────

const getAutorun: Operation<void, void, void, AutorunView> = {
  method: "GET",
  path: () => "/api/compilations/autorun",
  response: autorunView,
};

const setAutorunBody = z.object({
  enabled: z.boolean(),
  budgetTokens: z.number().int().positive().max(2_000_000).optional(),
});
export type SetAutorunBody = z.infer<typeof setAutorunBody>;

const setAutorun: Operation<void, void, SetAutorunBody, AutorunView> = {
  method: "POST",
  path: () => "/api/compilations/autorun",
  body: setAutorunBody,
  response: autorunView,
  invalidates: [getAutorun],
};

export const compilations = {
  list,
  get,
  adjudicate,
  setStatus,
  source,
  epoch,
  promote,
  demote,
  getAutorun,
  setAutorun,
};
